import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { NotificationResponse } from '@orion/contracts';
import { EmptyState } from '@orion/ui';
import { PageHeader } from '../../app/shell.js';
import { useMarkAllNotificationsRead, useMarkNotificationRead, useNotificationsQuery, useUnreadCountQuery } from './api.js';
import { notificationLink } from './links.js';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/** design/orion-crm-design/screenshots/Notifications.png. */
export function NotificationsPage() {
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const { data, isLoading } = useNotificationsQuery(filter === 'unread');
  const { data: unread } = useUnreadCountQuery();
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();
  const navigate = useNavigate();

  const notifications = data?.data ?? [];

  const openNotification = (n: NotificationResponse) => {
    if (!n.readAt) markRead.mutate(n.id);
    const link = notificationLink(n);
    if (link) navigate({ to: link.to, params: link.params } as never);
  };

  return (
    <>
      <PageHeader
        title="Notifications"
        actions={
          (unread?.count ?? 0) > 0 && (
            <button type="button" onClick={() => markAllRead.mutate()} className="text-sm font-semibold text-primary hover:underline">
              Mark all read
            </button>
          )
        }
      />
      <main className="flex flex-1 flex-col gap-4 p-7">
        <div className="flex overflow-hidden rounded-control border border-border-strong text-sm font-medium w-fit">
          {(['all', 'unread'] as const).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={filter === f ? 'bg-primary px-3.5 py-1.5 text-white' : 'bg-surface px-3.5 py-1.5 text-text-secondary hover:bg-surface-subtle'}
            >
              {f === 'all' ? 'All' : 'Unread'}
            </button>
          ))}
        </div>

        {isLoading && <p className="text-sm text-text-muted">Loading…</p>}

        {!isLoading && notifications.length === 0 && (
          <EmptyState title={filter === 'unread' ? "You're all caught up" : 'No notifications yet'} description="Assignments, mentions and reminders will show up here." />
        )}

        {notifications.length > 0 && (
          <div className="overflow-hidden rounded-card border border-border bg-surface">
            {notifications.map((n) => {
              const link = notificationLink(n);
              return (
                <div
                  key={n.id}
                  role={link ? 'button' : undefined}
                  tabIndex={link ? 0 : undefined}
                  onClick={() => openNotification(n)}
                  onKeyDown={(e) => link && (e.key === 'Enter' || e.key === ' ') && openNotification(n)}
                  className={`flex items-start justify-between gap-4 border-b border-divider px-5 py-3.5 last:border-b-0 ${link ? 'cursor-pointer hover:bg-surface-subtle' : ''} ${!n.readAt ? 'bg-primary-tint' : ''}`}
                >
                  <div className="flex min-w-0 items-start gap-3">
                    {!n.readAt && <span className="mt-1.5 size-2 shrink-0 rounded-full bg-notification-dot" />}
                    <div className={n.readAt ? 'ml-5' : ''}>
                      <p className="text-sm font-medium text-ink">{n.title}</p>
                      {n.body && <p className="mt-0.5 text-sm text-text-secondary">{n.body}</p>}
                    </div>
                  </div>
                  <span className="shrink-0 whitespace-nowrap text-xs text-text-muted">{formatDate(n.createdAt)}</span>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </>
  );
}
